import { composeProgress } from "apollo/utils";
import {
  FILE_TABLE_ACTION_UPLOAD_HIS,
  QUERY_PROGRESS_NOT_STARTED,
} from "core/consts";
import { useIntParams } from "core/model/utils/urls";
import { FileParams, FileState, Patient } from "core/types";
import { GeneralListAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { useHL7ImportStatusContext } from "dsl/atoms/HL7ImportStatusContext";
import { FileFeatureData } from "./useFilesFeature";
import { useGetFilteredFiles } from "./useGetFilteredFiles";
import { useGetFilteredFilesHIS } from "./useGetFilteredFilesHIS";
import { useGetFormFiles } from "./useGetFormFiles";
import { useMergedFiles } from "./useMergedFiles";

export function useGetFilesFeatures({
  action,
  externalId,
  isEligiblePage,
  isMessenger,
  params,
}: {
  action: GeneralListAction<FileFeatureData> | undefined;
  externalId: Patient["external_id"];
  isEligiblePage?: boolean;
  isMessenger?: boolean;
  params: FileParams;
  skipForms?: boolean;
}) {
  const { hasOneWaySync } = useHL7ImportStatusContext();
  const { auctionId, consultationId, patientId } = useIntParams<{
    auctionId: number;
    patientId: number;
    consultationId: number;
  }>();
  const [getFilesProgress, { receivedFiles, uploadedFiles }] =
    useGetFilteredFiles({ params });

  const shouldSkipFetchFilesHIS = (() => {
    if (!hasOneWaySync || isEligiblePage) return true;
    if (isMessenger) {
      return !externalId || action?.type !== FILE_TABLE_ACTION_UPLOAD_HIS;
    } else {
      return !externalId;
    }
  })();

  const [getFilesHISProgress, { availableFiles, unavailableFiles }] =
    useGetFilteredFilesHIS({
      externalId: externalId ?? "",
      skip: shouldSkipFetchFilesHIS,
    });

  const { allForms, getFormsProgress } = useGetFormFiles({
    auctionId,
    consultationId,
    isEligiblePage,
    patientId,
  });

  const { formsAsFiles, queryProgress, uploadedFilesV2 } = useMergedFiles({
    allForms,
    auctionId,
    availableFiles,
    getFilesHISProgress: shouldSkipFetchFilesHIS
      ? QUERY_PROGRESS_NOT_STARTED
      : getFilesHISProgress,
    getFilesProgress,
    getFormsProgress,
    isEligiblePage: !!isEligiblePage,
    uploadedFiles,
  });

  const mergedAvailableFiles: FileState[] | null = (() => {
    if (isMessenger && formsAsFiles) {
      const hisFiles = availableFiles ?? [];
      const finalUploadedFiles = uploadedFiles ?? [];
      const filteredFormsAsFiles = formsAsFiles.filter(
        ({ recare_file_type }) =>
          !finalUploadedFiles.some(
            (file) =>
              file.recare_file_type &&
              file.recare_file_type === recare_file_type,
          ),
      );
      return [...hisFiles, ...filteredFormsAsFiles];
    }
    return availableFiles;
  })();

  return {
    availableFiles: mergedAvailableFiles,
    getFilesHISProgress,
    queryProgress: composeProgress([
      queryProgress,
      getFormsProgress,
      getFormsProgress,
    ]),
    receivedFiles,
    unavailableFiles,
    uploadedFiles,
    uploadedFilesV2,
  };
}
